import { useCallback } from "react";
import {
  List,
  ActionIcon,
  Flex,
  Divider,
  Text,
  useMantineTheme,
  Box,
} from "@mantine/core";
import { IconPlaylistAdd } from "@tabler/icons";
import ActionCard from "src/components/ActionCard";
import { closeAllModals, useModals } from "@mantine/modals";
import { CardRadius, HeightCard, PATH_NAMES } from "src/utils/constants";
import {
  GetLocationTaxRatesQuery,
  useDeleteLocationTaxRateMutation,
  useGetLocationTaxRatesQuery,
} from "src/generated/graphql";
import TaxRateDetail from "./TaxRateDetail";
import { useParams } from "react-router-dom";
import { useAppContext } from "src/api/AppContext";
import useTransitionMenu from "src/hooks/useTransitionMenu";

export type ITaxRateElement =
  NonNullable<GetLocationTaxRatesQuery>["locationTaxRateMany"][0];

type IProps = {
  isAllowWrite: boolean;
};

function TaxRate({ isAllowWrite }: IProps) {
  const { TransitionMenu, setActiveOption } = useTransitionMenu();
  const { currentLocation } = useAppContext();
  const { locId = "" } = useParams();
  const currentlocId =
    locId === PATH_NAMES.GENERAL_LOC ? currentLocation?.id : locId;
  const modals = useModals();
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];

  const { data: taxRates, refetch } = useGetLocationTaxRatesQuery({
    variables: {
      locationId: currentlocId || "",
    },
    skip: !currentlocId,
  });

  const [deleteFees] = useDeleteLocationTaxRateMutation({
    onCompleted: () => {
      refetch();
      closeAllModals();
    },
  });

  const onCreate = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Location Tax Rate",
      children: (
        <TaxRateDetail
          refetch={refetch}
          locationId={locId}
          locTaxes={taxRates?.locationTaxRateMany || []}
        />
      ),
    });
  }, [refetch, locId, taxRates?.locationTaxRateMany]);

  const onEdit = useCallback(
    (detail: ITaxRateElement) => {
      modals.openModal({
        closeOnClickOutside: false,
        title: `Edit Location Tax Rate`,
        children: (
          <TaxRateDetail
            refetch={refetch}
            locationId={locId}
            formData={detail}
            locTaxes={taxRates?.locationTaxRateMany || []}
          />
        ),
      });
    },
    [refetch, locId, taxRates?.locationTaxRateMany]
  );

  const onDelete = (id: string) => {
    deleteFees({ variables: { deleteLocationTaxRateId: id } });
  };

  const handleOpenListMenu = (option: ITaxRateElement) => {
    setActiveOption(option);
  };

  return (
    <ActionCard
      title="Tax Rates"
      rightIconButton={
        isAllowWrite ? (
          <ActionIcon
            size="lg"
            onClick={() => onCreate()}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            <IconPlaylistAdd color={theme.colors.primaryTextColor[6]} />
          </ActionIcon>
        ) : (
          <></>
        )
      }
      height={HeightCard.sm}
      radius={CardRadius.sm}
      size={"sm"}
      overflow="hidden"
    >
      <Box>
        {!taxRates?.locationTaxRateMany.length ? (
          <Flex justify="center" align="center">
            <Text color={color}>Click</Text>&nbsp;
            {
              <ActionIcon
                size="lg"
                onClick={() => onCreate()}
                disabled={!isAllowWrite}
                sx={{
                  "&:hover": {
                    backgroundColor: theme.colors.secondary[4],
                  },
                }}
              >
                <IconPlaylistAdd color={theme.colors.primaryTextColor[6]} />
              </ActionIcon>
            }
            &nbsp;
            <Text color={color}>to add a Location Tax Rate</Text>
          </Flex>
        ) : (
          <List w="100%" listStyleType="none" style={{ color }}>
            {taxRates?.locationTaxRateMany.map((item, index) => (
              <List.Item
                w="100%"
                icon={null}
                key={item.id}
                c={color}
                sx={{
                  "&": {
                    position: "relative",
                  },
                  "& ::marker": {
                    content: "none",
                  },
                  "& .mantine-List-itemWrapper": {
                    display: "flex",
                  },
                  "& .mantine-List-itemWrapper > span > div": {
                    alignItems: "center",
                  },
                }}
              >
                {!!index && (
                  <Divider style={{ borderColor: theme.colors.gray[2] }} />
                )}
                <Flex
                  align="center"
                  justify="space-between"
                  sx={{
                    padding: "4px 0",
                  }}
                >
                  <Text size={16} sx={{ width: "200px" }}>
                    {item.organizationTaxRate.name}
                  </Text>

                  <Text fw={500} sx={{ width: "150px" }}>
                    Default: {item.taxRate}%
                  </Text>
                  {isAllowWrite && (
                    <TransitionMenu
                      onClick={() => handleOpenListMenu(item)}
                      onEdit={() => onEdit(item)}
                      onDelete={onDelete}
                      id={item.id}
                    />
                  )}
                </Flex>
              </List.Item>
            ))}
          </List>
        )}
      </Box>
    </ActionCard>
  );
}

export default TaxRate;
